// frontend/src/hooks/useOnboarding.ts
// React Query hooks for the AI onboarding module.
//
// Read hooks never throw (the service layer already degrades to empty shapes),
// so the wizard renders offline instead of blanking out. Write hooks surface
// failures through Sonner toasts, per the project's no-alert() rule.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import useLocationStore from '../stores/locationStore';
import * as service from '../services/onboardingService';
import { toastApiError } from '../services/api';
import type { DraftQuery } from '../services/onboardingService';
import type { SourceDocumentKind } from '../types/onboarding';

export const onboardingKeys = {
  all: ['onboarding'] as const,
  status: (locationId?: string) => [...onboardingKeys.all, 'status', locationId] as const,
  sessions: (locationId?: string) => [...onboardingKeys.all, 'sessions', locationId] as const,
  session: (id?: string) => [...onboardingKeys.all, 'session', id] as const,
  drafts: (sessionId?: string, query?: DraftQuery) =>
    [...onboardingKeys.all, 'drafts', sessionId, query] as const,
  questions: (sessionId?: string) => [...onboardingKeys.all, 'questions', sessionId] as const,
  parameters: (locationId?: string) => [...onboardingKeys.all, 'parameters', locationId] as const,
  audit: (locationId?: string, sessionId?: string | null) =>
    [...onboardingKeys.all, 'audit', locationId, sessionId] as const,
  costs: (locationId?: string) => [...onboardingKeys.all, 'costs', locationId] as const,
  posItems: (locationId?: string) => [...onboardingKeys.all, 'posItems', locationId] as const,
};

function useLocationId(): string | undefined {
  return useLocationStore((state) => state.currentLocation?.id);
}

// =============================================================================
// Status & sessions
// =============================================================================

export function useOnboardingStatus() {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.status(locationId),
    enabled: !!locationId,
    queryFn: service.fetchOnboardingStatus,
    staleTime: 30 * 1000,
  });
}

export function useSessions() {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.sessions(locationId),
    enabled: !!locationId,
    queryFn: service.fetchSessions,
  });
}

export function useSession(sessionId?: string) {
  return useQuery({
    queryKey: onboardingKeys.session(sessionId),
    enabled: !!sessionId,
    queryFn: () => service.fetchSession(sessionId as string),
    // While documents are being extracted the list changes under the user;
    // a short poll keeps the queue honest without a websocket.
    refetchInterval: (query) => {
      const data = query.state.data;
      // safeGet résout null sur un échec transitoire (réseau, 5xx) : sans ce
      // cas, UN seul raté arrêtait le polling pour toujours en pleine lecture.
      if (data === null) return 5000;
      const busy = data?.documents?.some((d) => d.status === 'EXTRACTING');
      if (busy) return 3000;
      // Recalcul de session en tâche de fond (lancé d'ici ou d'un autre
      // onglet) : suivre de près, le bouton reste désactivé partout.
      if (data?.recalcul?.enCours) return 2000;
      // While documents are being gathered (possibly from a phone via the QR
      // drop), keep the list fresh without a websocket.
      if (data?.status === 'DRAFT' || data?.status === 'INGESTING') return 5000;
      return false;
    },
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: (name?: string) => service.createSession(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.sessions(locationId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status(locationId) });
      toast.success('Nouvelle intégration démarrée.');
    },
    onError: (error) => toastApiError(error, 'Impossible de démarrer l’intégration.'),
  });
}

export function useUpdateSession(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { stage?: string; status?: string }) =>
      service.updateSession(sessionId as string, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
    },
  });
}

// =============================================================================
// Documents
// =============================================================================

export function useUploadDocuments(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => service.uploadDocuments(sessionId as string, files),
    onSuccess: (outcome) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      const documents = outcome.documents;
      // Les échecs sont NOMMÉS : l'utilisateur sait exactement quoi redéposer
      // (ou compresser) au lieu de deviner devant un compte incomplet.
      for (const failure of outcome.failures) {
        toast.error(`Non déposé (${failure.reason}) : ${failure.files.join(', ')}`, { duration: 12000 });
      }
      for (const warning of outcome.warnings) {
        toast.warning(`Ignoré du zip : ${warning}`, { duration: 10000 });
      }
      if (documents.length === 0 && (outcome.failures.length > 0 || outcome.warnings.length > 0)) return;
      toast.success(
        documents.length === 1
          ? '1 document ajouté à la file.'
          : `${documents.length} documents ajoutés à la file.`
      );
    },
    onError: (error) => toastApiError(error, 'Envoi des documents impossible.'),
  });
}

export function useSetDocumentKind(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { documentId: string; kind: SourceDocumentKind }) =>
      service.setDocumentKind(vars.documentId, vars.kind),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) }),
    onError: (error) => toastApiError(error, 'Changement de type impossible.'),
  });
}

export function useExtractDocument(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => service.extractDocument(documentId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.questions(sessionId) });
      if (result.reread) {
        const r = result.reread;
        const parts = [
          `${r.paired} ligne${r.paired > 1 ? 's' : ''} mise${r.paired > 1 ? 's' : ''} à jour`,
          r.toReconfirm > 0 ? `${r.toReconfirm} à reconfirmer` : null,
          r.created > 0 ? `${r.created} nouvelle${r.created > 1 ? 's' : ''}` : null,
          r.deleted > 0 ? `${r.deleted} disparue${r.deleted > 1 ? 's' : ''}` : null,
        ].filter(Boolean);
        toast.success(`Relecture terminée : ${parts.join(' • ')}. Vos décisions sont conservées.`);
        return;
      }
      toast.success(
        `${result.draftsCreated} élément${result.draftsCreated > 1 ? 's' : ''} à valider` +
        (result.questionsCreated > 0 ? ` • ${result.questionsCreated} question(s)` : '')
      );
    },
    onError: (error) => toastApiError(error, 'Extraction impossible.'),
  });
}

export function useRematch(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => service.rematchSession(sessionId as string),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      // Le rapprochement relie des lignes et tranche des évidences : les
      // compteurs guidés (nettoyage, manquants) changent avec lui.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count'] });
      const parts = [];
      if (result.productsConfirmed > 0) parts.push(`${result.productsConfirmed} prix confirmés`);
      if (result.recipeLinesLinked > 0) parts.push(`${result.recipeLinesLinked} ingrédients reliés`);
      if (result.aiLinesLinked > 0) parts.push(`dont ${result.aiLinesLinked} par l’IA`);
      const passes = result.aiPasses;
      if (passes?.namesCanonicalized) parts.push(`${passes.namesCanonicalized} produits renommés proprement`);
      if (passes?.formatsRepaired) parts.push(`${passes.formatsRepaired} formats réparés`);
      if (passes?.menuItemsLinked) parts.push(`${passes.menuItemsLinked} items de menu reliés`);
      if (passes?.menuRecipesDeduced) parts.push(`${passes.menuRecipesDeduced} recettes déduites`);
      if (result.autoAccepted) parts.push(`${result.autoAccepted} évidences acceptées seules`);
      if (result.autoRejected) parts.push(`${result.autoRejected} lignes administratives rejetées seules`);
      if (result.familyPriced) parts.push(`${result.familyPriced} prix hérités de leur famille de format`);
      if (result.familyPending) parts.push(`${result.familyPending} prix en attente de validation`);
      if (result.menuColumnsCollapsed) parts.push(`${result.menuColumnsCollapsed} items de menu nettoyés (colonnes de prix)`);
      if (result.posMatched) parts.push(`${result.posMatched} items retrouvés dans la caisse Véloce`);
      if (result.posGrouped) parts.push(`${result.posGrouped} produits rattachés à un bouton générique de caisse`);
      if (result.posVariantsCreated) parts.push(`${result.posVariantsCreated} déclinaisons créées depuis la caisse`);
      const policies = result.policies;
      if (policies?.brandLinesLinked) parts.push(`${policies.brandLinesLinked} lignes reliées par vos marques par défaut`);
      if (policies?.garnishLinesSettled) parts.push(`${policies.garnishLinesSettled} garnitures réglées par votre politique`);
      if (policies?.rebatesApplied) parts.push(`${policies.rebatesApplied} ristournes brasseur appliquées`);
      toast.success(
        parts.length > 0
          ? `Rapprochement relancé : ${parts.join(' • ')}.`
          : 'Rapprochement relancé — rien de nouveau à relier pour l’instant.'
      );
    },
    onError: (error) => toastApiError(error, 'Rapprochement impossible.'),
  });
}

export function useMenuOverview(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: [...onboardingKeys.all, 'menu-overview', sessionId],
    enabled: !!sessionId && enabled,
    staleTime: 15_000,
    queryFn: () => service.fetchMenuOverview(sessionId as string),
  });
}

export function useSessionCoverage(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: [...onboardingKeys.all, 'coverage', sessionId],
    enabled: !!sessionId && enabled,
    staleTime: 15_000,
    queryFn: () => service.fetchSessionCoverage(sessionId as string),
  });
}

export function useAcceptSafe(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => service.acceptSafeDrafts(sessionId as string),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count'] });
      toast.success(
        result.accepted > 0
          ? `${result.accepted} élément${result.accepted > 1 ? 's' : ''} sûr${result.accepted > 1 ? 's' : ''} accepté${result.accepted > 1 ? 's' : ''}.`
          : 'Aucun élément sûr en attente.'
      );
    },
    onError: (error) => toastApiError(error, 'Acceptation impossible.'),
  });
}

export function useResetLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => service.resetLocation(),
    onSuccess: (r) => {
      // Tout a changé : caches d'onboarding ET d'inventaire.
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success(
        `Remise à zéro faite : ${r.resources} produits, ${r.recipes} recettes, ` +
        `${r.posItems} items de caisse, ${r.sessions} intégration(s) effacés — ` +
        `${r.parametersSeeded} paramètres par défaut re-semés.`,
        { duration: 12000 }
      );
    },
    onError: (error) => toastApiError(error, 'Remise à zéro impossible.'),
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => service.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all] });
      toast.success('Intégration supprimée.');
    },
    onError: (error) => toastApiError(error, 'Suppression impossible.'),
  });
}

export function useCreateMissingProducts(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: service.MissingProductInput[]) =>
      service.createMissingProducts(sessionId as string, items),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.questions(sessionId) });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count', sessionId] });
      const links = result.rematch.recipeLinesLinked;
      toast.success(
        `${result.created} produit${result.created > 1 ? 's' : ''} créé${result.created > 1 ? 's' : ''}` +
        (links > 0 ? ` • ${links} ingrédients reliés` : '') +
        (result.aiClassified ? ` • ${result.aiClassified} classé(s) par l’IA (à vérifier)` : '') +
        ' — saisissez les prix quand vous les avez, ou laissez les factures les confirmer.'
      );
    },
    onError: (error) => toastApiError(error, 'Création impossible.'),
  });
}

const EMPTY_RENORMALIZE: service.RenormalizeSessionSummary = {
  documents: 0, paired: 0, changed: 0, toReconfirm: 0, orphansKept: 0, deleted: 0, created: 0, linksReset: 0, failures: [], rematch: null,
};

/**
 * Recalcul de session : le serveur répond tout de suite (tâche de fond,
 * verrou exclusif par session) et l'on attend la fin par sondage de la
 * session — une requête tenue ouverte le temps du travail était coupée par
 * le reverse proxy pendant que le serveur continuait.
 */
async function renormalizeAndWait(sessionId: string): Promise<service.RenormalizeSessionSummary> {
  const start = await service.renormalizeSession(sessionId);
  if (!start.queued || !start.demarreLe) return EMPTY_RENORMALIZE;
  const deadline = Date.now() + 20 * 60 * 1000;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const session = await service.fetchSession(sessionId);
    const r = session?.recalcul;
    if (r && r.demarreLe === start.demarreLe && !r.enCours) {
      if (r.erreur) throw new Error(r.erreur);
      return (r.resultat as service.RenormalizeSessionSummary | null) ?? EMPTY_RENORMALIZE;
    }
    if (r && r.demarreLe > start.demarreLe) {
      // Un autre recalcul a pris la suite : son bilan sera affiché par l'onglet qui l'a lancé.
      return EMPTY_RENORMALIZE;
    }
    if (Date.now() > deadline) {
      throw new Error('Le recalcul prend plus de temps que prévu ; il continue sur le serveur — rechargez la page dans quelques minutes.');
    }
  }
}

export function useRenormalizeSession(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => renormalizeAndWait(sessionId as string),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.questions(sessionId) });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'coverage', sessionId] });
      if (r.documents === 0) {
        toast.info('Aucun document lu à recalculer.');
        return;
      }
      const parts = [
        `${r.documents} document${r.documents > 1 ? 's' : ''}`,
        `${r.paired} ligne${r.paired > 1 ? 's' : ''} mise${r.paired > 1 ? 's' : ''} à jour`,
        r.toReconfirm > 0 ? `${r.toReconfirm} à reconfirmer` : null,
        r.created > 0 ? `${r.created} nouvelle${r.created > 1 ? 's' : ''}` : null,
        r.deleted > 0 ? `${r.deleted} disparue${r.deleted > 1 ? 's' : ''}` : null,
        r.failures.length > 0 ? `${r.failures.length} en échec` : null,
      ].filter(Boolean);
      toast.success(`Recalcul terminé : ${parts.join(' • ')}. Vos décisions sont conservées.`);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      toastApiError(error, 'Recalcul impossible.');
    },
  });
}

export function useExtractAll(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options?: { reread?: boolean }) => service.extractAll(sessionId as string, options ?? {}),
    onSuccess: (result, options) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      if (result.alreadyRunning) {
        toast.info('La lecture est déjà en cours — suivez l’avancement ci-dessous.');
      } else if (result.queued === 0) {
        toast.info('Aucun document en attente de lecture.');
      } else if (options?.reread) {
        toast.success(
          `Relecture lancée : ${result.queued} document${result.queued > 1 ? 's' : ''}. ` +
          'Chaque lecture remplace l’ancienne ; vos décisions sont conservées et seul ce qui a changé revient à valider.'
        );
      } else {
        toast.success(
          `Lecture lancée : ${result.queued} document${result.queued > 1 ? 's' : ''}. ` +
          'Vous pouvez continuer à travailler — l’avancement s’affiche en direct.'
        );
      }
    },
    onError: (error) => toastApiError(error, 'Traitement de la file impossible.'),
  });
}

export function useDeleteDocument(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => service.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.session(sessionId) });
      toast.success('Document retiré.');
    },
    onError: (error) => toastApiError(error, 'Suppression impossible.'),
  });
}

// =============================================================================
// Validation queue
// =============================================================================

export function useDrafts(sessionId?: string, query: DraftQuery = {}) {
  return useQuery({
    queryKey: onboardingKeys.drafts(sessionId, query),
    enabled: !!sessionId,
    queryFn: () => service.fetchDrafts(sessionId as string, query),
    placeholderData: (previous) => previous,
  });
}

export function useReviewDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      draftId: string;
      action: 'accept' | 'reject' | 'edit';
      payload?: Record<string, unknown>;
      matchedResourceId?: string | null;
    }) => service.reviewDraft(vars.draftId, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'session'] });
      // Les compteurs des étapes guidées ne regardent que les brouillons
      // PENDING : chaque décision les change.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count'] });
    },
    onError: (error) => toastApiError(error, 'Enregistrement impossible.'),
  });
}

export function useBulkReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ids: string[]; action: 'accept' | 'reject' }) =>
      service.bulkReviewDrafts(vars.ids, vars.action),
    onSuccess: (result, vars) => {
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'drafts'] });
      queryClient.invalidateQueries({ queryKey: [...onboardingKeys.all, 'session'] });
      // Le nettoyage passe par ce chemin : sans ces invalidations, l'étape
      // guidée « Nettoyer (N) » restait affichée après le rejet des N lignes.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count'] });
      toast.success(
        `${result.updated} élément(s) ${vars.action === 'accept' ? 'acceptés' : 'rejetés'}.`
      );
      if (result.skipped?.length) {
        // A draft with an unresolved blocking problem is never accepted in
        // bulk — say which ones and why, rather than silently dropping them.
        toast.warning(
          `${result.skipped.length} élément(s) non acceptés : ${result.skipped[0]?.reason ?? ''}`,
          { description: result.skipped.map((s) => s.label).slice(0, 5).join(' • ') }
        );
      }
    },
    onError: (error) => toastApiError(error, 'Action groupée impossible.'),
  });
}

// =============================================================================
// Questions
// =============================================================================

export function useQuestions(sessionId?: string) {
  return useQuery({
    queryKey: onboardingKeys.questions(sessionId),
    enabled: !!sessionId,
    queryFn: () => service.fetchQuestions(sessionId as string),
  });
}

export function useAnswerQuestion(sessionId?: string) {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: (vars: { questionId: string; answer: string }) =>
      service.answerQuestion(vars.questionId, vars.answer),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.questions(sessionId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.parameters(locationId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.costs(locationId) });
      toast.success(
        result.parameterUpdated
          ? `Réponse enregistrée — le paramètre « ${result.parameterKey} » est mis à jour partout.`
          : 'Réponse enregistrée.'
      );
    },
    onError: (error) => toastApiError(error, 'Enregistrement de la réponse impossible.'),
  });
}

// =============================================================================
// Commit & audit
// =============================================================================

export function useCommitSession(sessionId?: string) {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: () => service.commitSession(sessionId as string),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      const { created } = result.commit;
      toast.success(
        `Écriture terminée : ${created.resources} produit(s), ${created.recipes} recette(s), ` +
        `${created.ingredients} ligne(s) d’ingrédient, ${created.posItems} item(s) de caisse.`
      );
      if (result.commit.errors.length > 0) {
        toast.warning(`${result.commit.errors.length} élément(s) non écrits — voir le rapport.`);
      }
      queryClient.invalidateQueries({ queryKey: onboardingKeys.audit(locationId) });
    },
    onError: (error) => toastApiError(error, 'Écriture impossible.'),
  });
}

export function useAudit(sessionId?: string | null) {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.audit(locationId, sessionId ?? null),
    enabled: !!locationId,
    queryFn: () => service.fetchAudit(sessionId),
  });
}

// =============================================================================
// Costs, parameters, POS
// =============================================================================

export function useCosts() {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.costs(locationId),
    enabled: !!locationId,
    queryFn: service.fetchCosts,
  });
}

export function useParameters() {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.parameters(locationId),
    enabled: !!locationId,
    queryFn: service.fetchParameters,
  });
}

export function useSaveParameter() {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: service.saveParameter,
    onSuccess: (parameter) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.parameters(locationId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.costs(locationId) });
      toast.success(`« ${parameter.label} » enregistré — les recettes concernées sont recalculées.`);
    },
    onError: (error) => toastApiError(error, 'Enregistrement du paramètre impossible.'),
  });
}

export function useDeleteParameter() {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: service.deleteParameter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.parameters(locationId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.costs(locationId) });
      toast.success('Paramètre supprimé.');
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'cleanup-count'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'missing-count'] });
    },
    onError: (error) => toastApiError(error, 'Suppression impossible.'),
  });
}

export function usePosItems() {
  const locationId = useLocationId();
  return useQuery({
    queryKey: onboardingKeys.posItems(locationId),
    enabled: !!locationId,
    queryFn: service.fetchPosItems,
  });
}

export function useSyncPosItems() {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: service.syncPosItems,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.posItems(locationId) });
      toast.success(`${result.created} item(s) créé(s), ${result.updated} mis à jour.`);
    },
    onError: (error) => toastApiError(error, 'Génération impossible.'),
  });
}

export function useUpdatePosItem() {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      service.updatePosItem(vars.id, vars.patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: onboardingKeys.posItems(locationId) }),
    onError: (error) => toastApiError(error, 'Mise à jour impossible.'),
  });
}

export function useTestAi() {
  return useMutation({
    mutationFn: service.testAiConnection,
    onSuccess: (result) =>
      toast.success(`Connexion établie avec ${result.model} (${result.latencyMs} ms).`),
    onError: (error) => toastApiError(error, 'Test de connexion échoué.'),
  });
}

// =============================================================================
// Initialisation du stock depuis les factures déposées
// =============================================================================

export function useStockInitStatus(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: ['onboarding', 'stock-init', sessionId],
    enabled: !!sessionId && enabled,
    staleTime: 15_000,
    queryFn: () => service.fetchStockInitStatus(sessionId as string),
  });
}

export function useStockInit(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => service.runStockInit(sessionId as string),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'stock-init', sessionId] });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.questions(sessionId) });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success(
        `Stock initialisé : ${result.created} mouvement${result.created > 1 ? 's' : ''} d’achat` +
        (result.skipped > 0 ? ` • ${result.skipped} déjà entré(s)` : '') +
        (result.unconvertible.length > 0 ? ` • ${result.unconvertible.length} format(s) inconvertible(s)` : '') + '.'
      );
    },
    onError: (error) => toastApiError(error, 'Initialisation du stock impossible.'),
  });
}
